const MOBILE_FOOTER_QUERY = '(max-width: 749px)';
const CLOSE_FALLBACK_MS = 240;

class BnqFooterAccordion extends HTMLElement {
  #abortController;
  #mediaQuery = window.matchMedia(MOBILE_FOOTER_QUERY);
  #observer;
  #groups = new Map();
  #syncFrame;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.#mediaQuery.addEventListener('change', this.#handleBreakpointChange, {
      signal: this.#abortController.signal,
    });

    this.#observer = new MutationObserver(this.#scheduleSync);
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#syncFrame);

    for (const controller of this.#groups.values()) {
      this.#destroyController(controller);
    }

    this.#groups.clear();
  }

  #handleBreakpointChange = () => {
    for (const controller of this.#groups.values()) {
      if (this.#mediaQuery.matches) {
        this.#setMobileState(controller);
      } else {
        this.#setDesktopState(controller);
      }
    }
  };

  #scheduleSync = () => {
    cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    const groups = this.#findNavigationGroups();
    const activeGroups = new Set(groups);

    for (const [group, controller] of this.#groups) {
      if (!activeGroups.has(group) || !group.isConnected) {
        this.#destroyController(controller);
        this.#groups.delete(group);
      }
    }

    groups.forEach((group, index) => {
      if (this.#groups.has(group)) return;

      const controller = this.#enhanceGroup(group, index);
      if (!controller) return;

      this.#groups.set(group, controller);

      if (this.#mediaQuery.matches) {
        this.#setMobileState(controller);
      } else {
        this.#setDesktopState(controller);
      }
    });

    if (this.#groups.size > 0) {
      this.dataset.bnqFooterAccordionReady = '';
    } else {
      delete this.dataset.bnqFooterAccordionReady;
    }
  }

  #findNavigationGroups() {
    const rows = this.querySelectorAll(
      '.shopify-section-group-footer-group .layout-panel-flex--row.section-content-wrapper'
    );
    const mainRow = [...rows].find((row) =>
      row.querySelector(':scope > .group-block--width-custom')
    );

    if (!mainRow) return [];

    return [...mainRow.children].filter((child) =>
      child.matches('.group-block--width-fill')
    );
  }

  #enhanceGroup(group, index) {
    const content = group.querySelector(':scope > .group-block-content');
    if (!content) return null;

    const heading = [...content.children].find((child) =>
      child.matches('.text-block')
    );
    if (!heading) return null;

    const panel = [...content.children].find((child) =>
      child.matches('accordion-custom.menu, .bnq-footer-links')
    );
    if (!panel) return null;

    const label = heading.textContent.trim();
    if (!label) return null;

    let button = content.querySelector(':scope > .bnq-footer-accordion__trigger');

    if (!button) {
      const panelId = panel.id || `BnqFooterAccordion-${this.#instanceId}-${index + 1}`;
      const labelElement = document.createElement('span');
      const iconTemplate = this.querySelector('template[data-bnq-footer-accordion-icon]');

      button = document.createElement('button');
      button.type = 'button';
      button.className = 'bnq-footer-accordion__trigger';
      button.hidden = true;
      button.setAttribute('aria-controls', panelId);
      button.setAttribute('aria-expanded', 'false');

      labelElement.className = 'bnq-footer-accordion__label';
      labelElement.textContent = label;
      button.append(labelElement);

      if (iconTemplate) {
        button.append(iconTemplate.content.cloneNode(true));
      }

      panel.id = panelId;
      content.insertBefore(button, panel);
    }

    panel.classList.add('bnq-footer-accordion__panel');
    group.dataset.bnqFooterAccordion = '';

    const abortController = new AbortController();
    const controller = {
      abortController,
      button,
      closeTimer: 0,
      group,
      panel,
      transitionToken: 0,
    };

    button.addEventListener('click', () => this.#toggle(controller), {
      signal: abortController.signal,
    });

    return controller;
  }

  get #instanceId() {
    if (!this.dataset.bnqFooterAccordionId) {
      this.dataset.bnqFooterAccordionId =
        globalThis.crypto?.randomUUID?.().slice(0, 8) ||
        Math.random().toString(36).slice(2, 10);
    }

    return this.dataset.bnqFooterAccordionId;
  }

  #toggle(controller) {
    if (!this.#mediaQuery.matches) return;

    if (controller.button.getAttribute('aria-expanded') === 'true') {
      this.#close(controller);
    } else {
      this.#open(controller);
    }
  }

  #open(controller) {
    controller.transitionToken += 1;
    window.clearTimeout(controller.closeTimer);

    controller.panel.hidden = false;
    controller.panel.removeAttribute('inert');
    controller.panel.removeAttribute('aria-hidden');
    controller.button.setAttribute('aria-expanded', 'true');

    void controller.panel.offsetHeight;
    controller.group.dataset.bnqFooterAccordionOpen = '';
  }

  #close(controller, immediately = false) {
    const transitionToken = ++controller.transitionToken;
    window.clearTimeout(controller.closeTimer);

    delete controller.group.dataset.bnqFooterAccordionOpen;
    controller.button.setAttribute('aria-expanded', 'false');
    controller.panel.setAttribute('inert', '');
    controller.panel.setAttribute('aria-hidden', 'true');

    const finishClosing = (event) => {
      if (
        event &&
        (event.target !== controller.panel || event.propertyName !== 'block-size')
      ) {
        return;
      }

      controller.panel.removeEventListener('transitionend', finishClosing);

      if (
        controller.transitionToken === transitionToken &&
        controller.button.getAttribute('aria-expanded') === 'false'
      ) {
        controller.panel.hidden = true;
      }
    };

    if (immediately || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClosing();
      return;
    }

    controller.panel.addEventListener('transitionend', finishClosing, {
      signal: controller.abortController.signal,
    });
    controller.closeTimer = window.setTimeout(finishClosing, CLOSE_FALLBACK_MS);
  }

  #setMobileState(controller) {
    controller.button.hidden = false;
    this.#close(controller, true);
  }

  #setDesktopState(controller) {
    controller.transitionToken += 1;
    window.clearTimeout(controller.closeTimer);

    controller.button.hidden = true;
    controller.button.setAttribute('aria-expanded', 'true');
    delete controller.group.dataset.bnqFooterAccordionOpen;
    controller.panel.hidden = false;
    controller.panel.removeAttribute('inert');
    controller.panel.removeAttribute('aria-hidden');
  }

  #destroyController(controller) {
    window.clearTimeout(controller.closeTimer);
    controller.abortController.abort();
  }
}

if (!customElements.get('bnq-footer-accordion')) {
  customElements.define('bnq-footer-accordion', BnqFooterAccordion);
}
